import React, { useEffect, useState } from "react";
import { ShoppingCart, Minus, Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { useI18n } from "@/context/I18nContext";
import { formatPrice } from "@/lib/currency";
import { toast } from "sonner";

export default function CartView({ onChanged }) {
  const { t } = useI18n();
  const [cart, setCart] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/market/cart").then(({ data }) => { setCart(data); onChanged && onChanged(data.count); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setQty = async (item, qty) => {
    try { const { data } = await api.put("/market/cart/item", { product_id: item.product_id, variant_key: item.variant_key, qty }); setCart(data); onChanged && onChanged(data.count); }
    catch { toast.error(t("m_could_not_save")); }
  };
  const checkout = async () => {
    try { setBusy(true); const { data } = await api.post("/market/cart/checkout", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || t("m_could_not_checkout")); setBusy(false); }
  };

  if (!cart) return <div className="text-center py-10 text-[var(--rc-muted)]">{t("m_loading")}</div>;
  if (!cart.items.length) return (
    <div className="text-center py-20 text-[var(--rc-muted)]" data-testid="cart-empty">
      <ShoppingCart size={40} className="mx-auto mb-3 text-[var(--rc-gold)]" />
      <p>{t("m_cart_empty")}</p><p className="text-xs mt-1">{t("m_cart_empty_hint")}</p>
    </div>
  );
  const cur = cart.currency;

  return (
    <div className="max-w-2xl" data-testid="cart-view">
      <div className="space-y-2">
        {cart.items.map((it) => (
          <div key={`${it.product_id}-${it.variant_key || ""}`} className="flex gap-3 rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-3" data-testid={`cart-item-${it.product_id}`}>
            <div className="h-16 w-16 shrink-0 rounded-xl overflow-hidden bg-[var(--rc-bg)]">
              {it.image ? <img src={fileUrl(it.image)} alt="" className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center text-2xl">🛍️</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{it.title}</div>
              {it.variant_key && <div className="text-xs text-[var(--rc-muted)]">{it.variant_key}</div>}
              <div className="text-sm text-[var(--rc-gold)] font-semibold">{formatPrice(it.unit_price, cur)} <span className="text-[var(--rc-muted)] font-normal text-xs">{t("m_each")}</span></div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex items-center rounded-lg border border-[var(--rc-border)]">
                  <button onClick={() => setQty(it, it.qty - 1)} data-testid={`cart-dec-${it.product_id}`} className="h-8 w-8 grid place-items-center text-[var(--rc-gold)]"><Minus size={14} /></button>
                  <span className="w-8 text-center text-sm" data-testid={`cart-qty-${it.product_id}`}>{it.qty}</span>
                  <button onClick={() => setQty(it, it.qty + 1)} disabled={it.available != null && it.qty >= it.available} data-testid={`cart-inc-${it.product_id}`} className="h-8 w-8 grid place-items-center text-[var(--rc-gold)] disabled:opacity-30"><Plus size={14} /></button>
                </div>
                <button onClick={() => setQty(it, 0)} data-testid={`cart-remove-${it.product_id}`} className="text-red-400 flex items-center gap-1 text-xs"><Trash2 size={14} /> {t("m_remove")}</button>
                <span className="ml-auto font-semibold">{formatPrice(it.line_total, cur)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-4 space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-[var(--rc-muted)]">{t("m_subtotal")}</span><span data-testid="cart-subtotal">{formatPrice(cart.subtotal, cur)}</span></div>
        {cart.tax_percent > 0 && <div className="flex justify-between"><span className="text-[var(--rc-muted)]">{t("m_tax")} ({cart.tax_percent}%)</span><span data-testid="cart-tax">{formatPrice(cart.tax, cur)}</span></div>}
        <div className="flex justify-between text-base font-semibold pt-1 border-t border-[var(--rc-border)]"><span>{t("m_total")}</span><span className="text-[var(--rc-gold)]" data-testid="cart-total">{formatPrice(cart.total, cur)}</span></div>
      </div>

      <button onClick={checkout} disabled={busy} data-testid="cart-checkout" className="w-full mt-4 rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 flex items-center justify-center gap-2">
        {busy ? <Loader2 size={17} className="animate-spin" /> : <ShoppingCart size={17} />} {busy ? t("m_processing") : `${t("m_checkout")} · ${formatPrice(cart.total, cur)}`}
      </button>
    </div>
  );
}
